import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'src/store/store';
import i18n from 'src/utils/translate';
import { pluralize } from 'src/utils/string';
import { setAlert } from 'src/components/alert/actions';
import Button from 'src/components/buttons/button';
import { NestedReplies } from 'src/constants/types';
import { titleMedium, textLight } from 'styles-js/mixins/typography';
import { nestPostReplies } from 'src/utils/dataStructure/restructurePostResponses';
import PrivatePostMessage from 'src/modules/community/singlePost/components/privatePostMessage';
import InteractiveReplyItem from 'src/modules/community/singlePost/components/replies/interactiveReplyItem';

export default function Responses(): JSX.Element {
  const post = useSelector(({ pages }) => pages.singlePost.post);
  const currentUser = useSelector(({ context }) => context.currentUser);
  const [responses, isReversed, doReverse] = useNestedResponses();
  const responsesRef = useScrollToResponses();
  const isPrivate = post.isPrivate && !currentUser?.isCommunityMemberOrHUStaff;
  useShowBannerForDeletedReply();

  return (
    <>
      <Header ref={responsesRef}>
        {post?.totalResponses > 0 && (
          <>
            <ReplyCount>
              {pluralize(i18n.t('%1$s Reply'), i18n.t('%1$s Replies'), post?.totalResponses)}
            </ReplyCount>
            {!isPrivate && (
              <SortButtons>
                <Button
                  className={isReversed ? '' : 'selected'}
                  type="link"
                  onClick={doReverse}
                >
                  {i18n.t('oldest')}
                </Button>
                <span>•</span>
                <Button
                  className={isReversed ? 'selected' : ''}
                  type="link"
                  onClick={doReverse}
                >
                  {i18n.t('newest')}
                </Button>
              </SortButtons>
            )}
          </>
        )}
      </Header>

      {!isPrivate ? (
        responses.map((response) => (
          <InteractiveReplyItem
            currentResponse={response}
            indent={response.indent}
            key={response.id}
          />
        ))
      ) : (
        <PrivatePostMessage>
          <p>{i18n.t('You need to be a member of this community to see hidden responses.')}</p>
        </PrivatePostMessage>
      )}
    </>
  );
}

const useShowBannerForDeletedReply = () => {
  const dispatch = useDispatch();
  const { query } = useRouter();
  const responseId = Number(query?.responses as string);
  const { responses } = useSelector(({ pages }) => pages.singlePost.post);

  useEffect(() => {
    if (responseId && !responses.map(response => response.id).includes(responseId)) {
      dispatch(setAlert(i18n.t('This reply was deleted')));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
};

const useScrollToResponses = () => {
  const { query } = useRouter();
  const responses = query?.responses as string;
  const responsesRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (responses !== '') return;
    const timeout = setTimeout(() => {
      responsesRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 300); // wait for featured posts to load
    return () => clearTimeout(timeout);
  }, [responses]);

  return responsesRef;
};

function useNestedResponses(): [NestedReplies[], boolean, () => void] {
  const { id, responses } = useSelector(({ pages }) => pages.singlePost.post);
  const memoizedResponses = useMemo(() => nestPostReplies(id, responses), [id, responses]);
  const [restructuredResponses, setRestructuredResponses] = useState(memoizedResponses);

  useEffect(() => {
    setRestructuredResponses(memoizedResponses);
  }, [memoizedResponses]);

  const [isReversed, setIsReversed] = useState(false);
  const doReverse = useCallback(() => {
    setRestructuredResponses(restructuredResponses.reverse());
    setIsReversed(!isReversed);
  }, [restructuredResponses, isReversed]);

  return [restructuredResponses, isReversed, doReverse];
}

const Header = styled.div`
  scroll-margin-top: 60px;
  margin-bottom: 5px;
  padding-bottom: 10px;
  border-bottom: 1px solid ${({ theme }) => theme.colorGreyLight};
  display: flex;
  justify-content: space-between;
`;

const ReplyCount = styled.div`
  ${titleMedium}
`;

const SortButtons = styled.div`
  ${textLight}
  display: flex;
  align-items: center;
  button {
    ${textLight};
    margin: 0;
    padding: 3px 6px;
    transition: none;
    &:focus,
    &:hover {
      color: ${({ theme }) => theme.colorTextBlack};
    }
    &.selected {
      color: ${({ theme }) => theme.colorTextBlack};
      font-weight: ${({ theme }) => theme.fontWeightBold};
    }
  }
`;
